import React, { useState, useEffect, useMemo } from 'react';
import { Form, Input, Select, Card, DatePicker, InputNumber, Space, Row, Col, message, Button, Modal, Checkbox, Radio, App, Typography, Breadcrumb, Progress, Tooltip, Alert, Badge } from 'antd';
import { useAuth0 } from '@auth0/auth0-react';
import { useNavigate } from 'react-router-dom';
import { apiClient } from '../../utils/api';
import { theme } from '../../theme';
import { HomeOutlined, FileTextOutlined, SearchOutlined, CheckCircleOutlined, WarningOutlined, CloseCircleOutlined, InfoCircleOutlined } from '@ant-design/icons';
import moment from 'moment';

const { Text } = Typography;

const { Option } = Select;

interface PadronOption {
  id: number;
  nc: string;
  denominacion: string;
}

interface PadronResponse {
  id: number;
  nc: string;
  denominacion: string;
  // otros campos que vengan del backend
}

// Definir tipos para los valores de calidad
type CalidadLetra = 'A' | 'B' | 'C' | 'D' | 'E' | 'F';
type CalidadFraccion = '' | '-1/4' | '-1/2' | '-3/4';

// Modificar las interfaces de mapeo con los tipos correctos
const CALIDAD_LETRA_VALORES: Record<CalidadLetra, number> = {
  'A': 100,
  'B': 200,
  'C': 300,
  'D': 400,
  'E': 500,
  'F': 600
};

const CALIDAD_FRACCION_VALORES: Record<CalidadFraccion, number> = {
  '': 0,
  '-1/4': 25,
  '-1/2': 50,
  '-3/4': 75
};

interface FardoRecord {
  checked: boolean;
  nroMuestra: string;
  calidad: string;
  calidadNumerica: number;
  micronaire: number;
  longitud: number;
  resistencia: number;
  isDuplicado?: boolean;
  lote: number;
}

interface ResumenRecord {
  id: number;
  texto: string;
  cantidadSeleccionada: number;
  totalRegistros: number;
  calidadNumerica: number;
  lote: number;
}

// Actualizar la interface FormValues con todos los campos necesarios
interface FormValues {
  // Campos de calidad
  calidad_letra: CalidadLetra;
  calidad_fraccion: CalidadFraccion;
  
  // Campos de identificación
  fecha: moment.Moment;
  nro_planilla: number;
  lote: number;
  
  // Campos de medición (asegurarnos de que son number)
  micronaire: number;
  longitud_hebra: number;
  resistencia: number;
  
  // Campos de muestras
  desde_muestra: number;
  hasta_muestra: number;
  
  // Campos de origen/cliente
  origen_id?: number;
  cliente_id?: number;
}

// Actualizar la interface RegistroClasificacion para que coincida con el backend
interface RegistroClasificacion {
  origen: string;
  cliente: string;
  lote: number;
  nroMuestra: string;
  calidad: string;
  calidadNumerica: number;
  micronaire: number;
  longitud: number;
  resistencia: number;
}

// Nueva interface para detalles (solo calidad)
interface DetalleClasificacion {
  lote: number;
  nroMuestra: string;
  calidad: string;
  calidadNumerica: number;
}

// Nueva interface para datos HVI
interface DatosHVI {
  nroMuestra: string;
  micronaire: number;
  longitud: number;
  resistencia: number;
}

// Actualizar la interface ClasificacionRequest
interface ClasificacionRequest {
  empresa_id: number;
  origen: string;
  cliente: string;
  fecha: string;
  nro_planilla: number;
  detalles: DetalleClasificacion[];
  datos_hvi: DatosHVI[];
}

// Definir un tipo para los campos numéricos
type CampoNumerico = 'micronaire' | 'longitud_hebra' | 'resistencia';

// Rangos HVI estándar según industria del algodón
const HVI_RANGES = {
  micronaire: {
    min: 2.5,
    max: 6.5,
    optimal: { min: 3.5, max: 4.9 },
    unit: 'mic'
  },
  longitud: {
    min: 20,
    max: 35,
    optimal: { min: 25, max: 32 },
    unit: 'mm'
  },
  resistencia: {
    min: 20,
    max: 45,
    optimal: { min: 28, max: 35 },
    unit: 'g/tex'
  }
};

// Función para evaluar el estado de un parámetro HVI
const evaluateHVIParameter = (param: 'micronaire' | 'longitud' | 'resistencia', value: number): 'optimal' | 'acceptable' | 'warning' | 'error' => {
  const range = HVI_RANGES[param];
  if (!value || value <= 0) return 'error';
  
  if (value >= range.optimal.min && value <= range.optimal.max) {
    return 'optimal';
  } else if (value >= range.min && value <= range.max) {
    return 'acceptable';
  } else if (value >= range.min * 0.9 && value <= range.max * 1.1) {
    return 'warning';
  }
  return 'error';
};

// Función para obtener el color según el estado
const getStatusColor = (status: 'optimal' | 'acceptable' | 'warning' | 'error'): string => {
  switch (status) {
    case 'optimal': return theme.colors.success;
    case 'acceptable': return theme.colors.info;
    case 'warning': return theme.colors.warning;
    case 'error': return theme.colors.error;
    default: return theme.colors.border;
  }
};

// Función para obtener el icono según el estado
const getStatusIcon = (status: 'optimal' | 'acceptable' | 'warning' | 'error') => {
  switch (status) {
    case 'optimal': return <CheckCircleOutlined style={{ color: theme.colors.success }} />;
    case 'acceptable': return <InfoCircleOutlined style={{ color: theme.colors.info }} />;
    case 'warning': return <WarningOutlined style={{ color: theme.colors.warning }} />;
    case 'error': return <CloseCircleOutlined style={{ color: theme.colors.error }} />;
  }
};

// Función para calcular Fiber Quality Index (FQI)
const calculateFQI = (longitud: number, resistencia: number, uniformidad: number = 82, micronaire: number): number => {
  if (!longitud || !resistencia || !micronaire || micronaire === 0) return 0;
  // Uniformidad por defecto 82 si no se proporciona
  return (longitud * resistencia * uniformidad) / micronaire;
};

// Función para calcular Spinning Consistency Index (SCI)
const calculateSCI = (longitud: number, resistencia: number, micronaire: number): number => {
  if (!longitud || !resistencia || !micronaire || micronaire === 0) return 0;
  return (resistencia * longitud) / micronaire;
};

// Funciones de estadísticas mejoradas
const calculateMean = (values: number[]): number => {
  if (values.length === 0) return 0;
  return values.reduce((sum, val) => sum + val, 0) / values.length;
};

const calculateMedian = (values: number[]): number => {
  if (values.length === 0) return 0;
  const sorted = [...values].sort((a, b) => a - b);
  const mid = Math.floor(sorted.length / 2);
  return sorted.length % 2 === 0
    ? (sorted[mid - 1] + sorted[mid]) / 2
    : sorted[mid];
};

const calculateStdDev = (values: number[]): number => {
  if (values.length === 0) return 0;
  const mean = calculateMean(values);
  const squaredDiffs = values.map(val => Math.pow(val - mean, 2));
  const avgSquaredDiff = calculateMean(squaredDiffs);
  return Math.sqrt(avgSquaredDiff);
};

const calculateCV = (values: number[]): number => {
  if (values.length === 0) return 0;
  const mean = calculateMean(values);
  if (mean === 0) return 0;
  const stdDev = calculateStdDev(values);
  return (stdDev / mean) * 100;
};

const calculateMinMax = (values: number[]): { min: number; max: number } => {
  if (values.length === 0) return { min: 0, max: 0 };
  return {
    min: Math.min(...values),
    max: Math.max(...values)
  };
};

// Función para calcular porcentaje dentro de rango óptimo
const calculateOptimalPercentage = (
  values: number[],
  param: 'micronaire' | 'longitud' | 'resistencia'
): number => {
  if (values.length === 0) return 0;
  const range = HVI_RANGES[param];
  const optimalCount = values.filter(
    val => val >= range.optimal.min && val <= range.optimal.max
  ).length;
  return (optimalCount / values.length) * 100;
};

const FormularioClasificacion: React.FC = () => {
  const { message: messageApi } = App.useApp();
  const [form] = Form.useForm();
  const { getAccessTokenSilently, user } = useAuth0();
  const navigate = useNavigate();
  const [padrones, setPadrones] = useState<PadronOption[]>([]);
  const [loading, setLoading] = useState(false);
  const [enviando, setEnviando] = useState(false);
  const [origenSelected, setOrigenSelected] = useState<boolean>(false);
  const [clienteSelected, setClienteSelected] = useState<boolean>(false);
  const [registros, setRegistros] = useState<FardoRecord[]>([]);
  const [totalChecked, setTotalChecked] = useState<number>(0);
  const [detalleContent, setDetalleContent] = useState<React.ReactNode>(null);
  const [resumenes, setResumenes] = useState<ResumenRecord[]>([]);
  const [camposBloqueados, setCamposBloqueados] = useState<boolean>(false);
  const [cabeceraFija, setCabeceraFija] = useState<boolean>(false);
  const [valorCLO, setValorCLO] = useState<number>(0);
  const [fardosEnLote, setFardosEnLote] = useState<number>(0);
  const [desdeMuestraValue, setDesdeMuestraValue] = useState<number | null>(null);
  const [hastaMuestraValue, setHastaMuestraValue] = useState<number | null>(null);
  const [selectedResumen, setSelectedResumen] = useState<number | null>(null);
  const [registrosParaGrabar, setRegistrosParaGrabar] = useState<FardoRecord[]>([]);
  const [canModify, setCanModify] = useState<boolean>(false);
  const [loteEnModificacion, setLoteEnModificacion] = useState<number | null>(null);

  // Estados para búsqueda de origen y cliente
  const [searchOrigen, setSearchOrigen] = useState('');
  const [showSearchOrigen, setShowSearchOrigen] = useState(false);
  const [selectedOrigen, setSelectedOrigen] = useState<PadronOption | null>(null);
  const [searchCliente, setSearchCliente] = useState('');
  const [showSearchCliente, setShowSearchCliente] = useState(false);
  const [selectedCliente, setSelectedCliente] = useState<PadronOption | null>(null);
 
  // Cargar datos del padrón al iniciar y preseleccionar fecha actual
  useEffect(() => {
    // Preseleccionar fecha actual
    form.setFieldsValue({
      fecha: moment()
    });

    const fetchPadrones = async () => {
      console.log('user', user)
      try {
        setLoading(true);
        const token = await getAccessTokenSilently();
        const response = await apiClient.get<PadronResponse[]>(
          `${process.env.REACT_APP_PADRON_URL}/leer_padron/${user?.id_empresa}`,
          { token }
        );

        const data = response.data || response;
        if (!Array.isArray(data)) {
          throw new Error('La respuesta no es un array');
        }

        const padronesData: PadronOption[] = data.map(padron => ({
          id: padron.id,
          nc: padron.nc,
          denominacion: padron.denominacion
        }));

        setPadrones(padronesData);
      } catch (error) {
        console.error('Error al cargar padrones:', error);
        messageApi.error('Error al cargar la lista de padrones');
      } finally {
        setLoading(false);
      }
    };

    const fetchSiguienteLote = async () => {
      try {
        const token = await getAccessTokenSilently();
        const resp = await apiClient.get<{ siguiente: number }>(
          `${process.env.REACT_APP_CLASIFICACION_URL}/siguiente_lote/${user?.id_empresa}/`,
          { token }
        );
        const sig = (resp as any).data?.siguiente ?? (resp as any).siguiente ?? 1;
        form.setFieldsValue({ lote: sig });
      } catch (error) {
        console.error('Error al obtener siguiente lote:', error);
      }
    };

    if (user?.id_empresa) {
      fetchPadrones();
      fetchSiguienteLote();
    }
  }, [user, getAccessTokenSilently, form]);

  // Cerrar dropdowns cuando se hace clic fuera
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as HTMLElement;
      if (!target.closest('.search-dropdown')) {
        setShowSearchOrigen(false);
        setShowSearchCliente(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  const validarCamposNumericos = (values: FormValues) => {
    const camposNumericos: CampoNumerico[] = ['micronaire', 'longitud_hebra', 'resistencia'];
    const errores: string[] = [];

    for (const campo of camposNumericos) {
        if (!values[campo] || values[campo] <= 0) {
            errores.push(`El campo ${campo.replace('_', ' ')} debe ser mayor que 0`);
        }
    }

    return errores;
  };

  // Validación inteligente de consistencia entre parámetros HVI
  const validarConsistenciaHVI = (values: FormValues): string[] => {
    const warnings: string[] = [];
    
    if (values.micronaire && values.longitud_hebra && values.resistencia) {
      // Validar relación esperada entre parámetros
      // Micronaire bajo generalmente se asocia con longitud alta
      if (values.micronaire < 3.5 && values.longitud_hebra < 28) {
        warnings.push('Advertencia: Micronaire bajo con longitud baja puede indicar inconsistencia');
      }
      
      // Resistencia muy alta con micronaire muy bajo puede ser inusual
      if (values.micronaire < 3.0 && values.resistencia > 40) {
        warnings.push('Advertencia: Combinación inusual de micronaire muy bajo con resistencia muy alta');
      }
      
      // Calcular FQI y SCI para validar consistencia
      const fqi = calculateFQI(values.longitud_hebra, values.resistencia, 82, values.micronaire);
      const sci = calculateSCI(values.longitud_hebra, values.resistencia, values.micronaire);
      
      // FQI muy bajo puede indicar problemas
      if (fqi < 100) {
        warnings.push('Advertencia: FQI muy bajo, revise los valores ingresados');
      }
      
      // SCI muy bajo también puede indicar problemas
      if (sci < 150) {
        warnings.push('Advertencia: SCI muy bajo, revise los valores ingresados');
      }
    }
    
    return warnings;
  };

  const handleSubmit = async (values: FormValues) => {
    try {
      setEnviando(true);
      
      // Debug: Imprimir los valores recibidos
      console.log('=== DEBUG HANDLE SUBMIT ===');
      console.log('Valores del formulario:', values);
      console.log('origen_id:', values.origen_id, 'tipo:', typeof values.origen_id);
      console.log('cliente_id:', values.cliente_id, 'tipo:', typeof values.cliente_id);
      console.log('lote:', values.lote, 'tipo:', typeof values.lote);
      console.log('selectedOrigen:', selectedOrigen);
      console.log('selectedCliente:', selectedCliente);
      console.log('origenSelected:', origenSelected);
      console.log('clienteSelected:', clienteSelected);
      
      const erroresNumericos = validarCamposNumericos(values);
      if (erroresNumericos.length > 0) {
        erroresNumericos.forEach(error => messageApi.error(error));
        return;
      }

      // Validaciones básicas - Ambos campos son obligatorios
      if (!values.origen_id) {
        messageApi.error('Debe seleccionar un Origen');
        return;
      }

      if (!values.cliente_id && values.cliente_id !== 0) {
        messageApi.error('Debe seleccionar un Cliente');
        return;
      }

      if (!values.desde_muestra || !values.hasta_muestra) {
        messageApi.error('Los campos Desde Muestra y Hasta Muestra son obligatorios');
        return;
      }

      if (values.hasta_muestra < values.desde_muestra) {
        messageApi.error('El número de muestra final debe ser mayor al inicial');
        return;
      }

      // Validaciones inteligentes de consistencia HVI (solo mostrar advertencias, no bloquear)
      const warningsHVI = validarConsistenciaHVI(values);
      if (warningsHVI.length > 0) {
        warningsHVI.forEach(warning => {
          messageApi.warning(warning);
        });
      }

      // Control previo de clasificación
      const token = await getAccessTokenSilently();
      const cantidadMuestras = values.hasta_muestra - values.desde_muestra + 1;
      const clienteIdForControl = values.cliente_id === 0 ? '0' : (values.cliente_id || '0');

      try {
        const controlResponse = await apiClient.get<any>(
          `${process.env.REACT_APP_CLASIFICACION_URL}/control_clasificacion/${user?.id_empresa}/${values.nro_planilla}/${values.lote}/${clienteIdForControl}/${cantidadMuestras}`,
          { token }
        );

        const controlData = controlResponse.data || controlResponse;

        // Mensajes de bloqueo → modal y stop
        if (controlData?.mensajes_bloqueo?.length > 0) {
          Modal.error({
            title: 'No se puede continuar',
            content: (
              <div>
                {controlData.mensajes_bloqueo.map((m: string, i: number) => <p key={i}>{m}</p>)}
              </div>
            ),
            okText: 'Aceptar',
          });
          return;
        }

        // Mensajes de aviso → warning suave, continúa
        if (controlData?.mensajes_aviso?.length > 0) {
          controlData.mensajes_aviso.forEach((m: string) => messageApi.warning(m, 5));
        }

        // Guardar CLO y fardos existentes para validar en handleResumen
        if (controlData?.data) {
          setValorCLO(controlData.data.valor_clo || 0);
          setFardosEnLote(controlData.data.fardos_en_lote || 0);
        }

        // Informar si el lote ya fue resumido en esta sesión (no bloquea, solo avisa)
        const loteIngresado: number = values.lote;
        const loteEnSession = registrosParaGrabar.some(r => r.lote === loteIngresado);
        if (loteEnSession) {
          messageApi.info(`El lote ${loteIngresado} ya tiene fardos en esta sesión. Los nuevos se acumularán en la tabla.`, 4);
        }

        // Informar si el lote ya tiene fardos en BD (es una continuación, no un error)
        const lotesEnDB: number[] = controlData?.data?.lotes_existentes || [];
        if (lotesEnDB.includes(loteIngresado)) {
          messageApi.info(`El lote ${loteIngresado} ya tiene fardos en esta planilla. Se agregarán los nuevos fardos al lote existente.`, 4);
        }

        // Usar los valores del formulario o los estados seleccionados como respaldo
        const origenId = values.origen_id || (selectedOrigen ? selectedOrigen.id : null);
        const clienteIdForRegistros = values.cliente_id !== undefined ? values.cliente_id : (selectedCliente ? selectedCliente.id : null);

        const origenStr = origenId ? origenId.toString() : '0';
        const clienteStr = clienteIdForRegistros !== null ? clienteIdForRegistros.toString() : '0';

        const calidad = `${values.calidad_letra}${values.calidad_fraccion}`;
        const calidadNumerica = CALIDAD_LETRA_VALORES[values.calidad_letra] + CALIDAD_FRACCION_VALORES[values.calidad_fraccion];

        // Generar registros para cada número del rango (los fardos pueden no existir aún)
        const registrosNuevos: FardoRecord[] = [];
        for (let i = values.desde_muestra; i <= values.hasta_muestra; i++) {
          registrosNuevos.push({
            checked: true,
            nroMuestra: `${origenStr}-${clienteStr}-${i}`,
            calidad,
            calidadNumerica,
            micronaire: values.micronaire,
            longitud: values.longitud_hebra,
            resistencia: values.resistencia,
            lote: values.lote
          });
        }

        // Acumular en tabla deduplicando por nroMuestra (multi-rango)
        setRegistros(prev => {
          const existentes = new Set(prev.map(r => r.nroMuestra));
          const nuevos = registrosNuevos.filter(r => !existentes.has(r.nroMuestra));
          const acumulados = [...prev, ...nuevos];
          setTotalChecked(acumulados.filter(r => r.checked).length);
          return acumulados;
        });

        // Solo bloquear Lote y Calidad; Desde/Hasta y filtros permanecen editables
        setCamposBloqueados(true);
        setCabeceraFija(true);

      } catch (error: any) {
        if (error.response?.data?.message) {
          messageApi.error(error.response.data.message);
        } else {
          messageApi.error('Error al realizar el control de clasificación');
        }
        return;
      }
    } catch (error) {
      console.error('Error al generar registros:', error);
      messageApi.error('Error al generar los registros');
    } finally {
      setEnviando(false);
    }
  };

  const handleGrabarTodo = async () => {
    try {
      console.log('=== INICIO GRABAR TODO ===');
      console.log('Registros a grabar:', registrosParaGrabar);
      console.log('Resúmenes finales:', resumenes);

      if (registrosParaGrabar.length === 0) {
        messageApi.warning('No hay registros para grabar');
        return;
      }

      const formValues = form.getFieldsValue() as FormValues;
      
      if (!formValues.fecha || !formValues.nro_planilla || !formValues.lote) {
        messageApi.error('Faltan datos obligatorios (fecha, número de planilla o lote)');
        return;
      }

      setLoading(true);
      const token = await getAccessTokenSilently();

      // Obtener los valores correctos de origen y cliente
      const origenId = formValues.origen_id || (selectedOrigen ? selectedOrigen.id : null);
      const clienteId = formValues.cliente_id !== undefined ? formValues.cliente_id : (selectedCliente ? selectedCliente.id : null);
      
      console.log('=== DEBUG GRABAR TODO ===');
      console.log('formValues.origen_id:', formValues.origen_id);
      console.log('selectedOrigen:', selectedOrigen);
      console.log('origenId final:', origenId);
      console.log('formValues.cliente_id:', formValues.cliente_id);
      console.log('selectedCliente:', selectedCliente);
      console.log('clienteId final:', clienteId);

      // Obtener los valores de origen y cliente una sola vez para toda la petición
      const origen = origenId ? origenId.toString() : '0';
      const cliente = clienteId !== null ? clienteId.toString() : '0';

      // Separar detalles (calidad) y datos HVI
      const detalles: DetalleClasificacion[] = registrosParaGrabar.map(registro => {
        const [origenString, clienteString, nro] = registro.nroMuestra.split('-');
        
        return {
          nroMuestra: nro,
          calidad: registro.calidad,
          calidadNumerica: registro.calidadNumerica,
          lote: registro.lote,
        };
      });

      const datosHVI: DatosHVI[] = registrosParaGrabar.map(registro => {
        const [origenString, clienteString, nro] = registro.nroMuestra.split('-');
        
        return {
          nroMuestra: nro,
          micronaire: registro.micronaire,
          longitud: registro.longitud,
          resistencia: registro.resistencia,
        };
      });

      const requestData: ClasificacionRequest = {
        empresa_id: user?.id_empresa || 0,
        origen,
        cliente,
        fecha: formValues.fecha.format('YYYY-MM-DD'),
        nro_planilla: formValues.nro_planilla,
        detalles,
        datos_hvi: datosHVI
      };

      await apiClient.post(
        `${process.env.REACT_APP_CLASIFICACION_URL}/crear_clasificacion/${user?.id_empresa}/REDUCIDA/`,
        requestData,
        { token }
      );
      
      // Si llegamos aquí, la operación fue exitosa
      // Limpiar estados y mostrar mensaje de éxito
      setRegistrosParaGrabar([]);
      setResumenes([]);
      setCamposBloqueados(false);
      setCabeceraFija(false);
      setValorCLO(0);
      setFardosEnLote(0);
      setLoteEnModificacion(null);
      form.resetFields();
      setRegistros([]);
      setTotalChecked(0);
      setDesdeMuestraValue(null);
      setHastaMuestraValue(null);
      setSelectedResumen(null);
      setOrigenSelected(false);
      setClienteSelected(false);
      setSelectedOrigen(null);
      setSelectedCliente(null);

      // Pre-cargar el siguiente número de lote para la próxima entrada
      try {
        const token2 = await getAccessTokenSilently();
        const resp = await apiClient.get<{ siguiente: number }>(
          `${process.env.REACT_APP_CLASIFICACION_URL}/siguiente_lote/${user?.id_empresa}/`,
          { token: token2 }
        );
        const sig = (resp as any).data?.siguiente ?? (resp as any).siguiente ?? 1;
        form.setFieldsValue({ lote: sig });
      } catch (_) {}

      messageApi.success('Todos los registros han sido grabados exitosamente');
    } catch (error: any) {
      console.error('Error al grabar todo:', error);
      
      // Manejar el error verificando la estructura del objeto error
      if (error.response?.data) {
        // Error con respuesta del servidor
        const errorMessage = error.response.data.error || error.response.data.message || 'Error del servidor';
        messageApi.error(`Error al grabar los registros: ${errorMessage}`);
      } else if (error.request) {
        // Error de red o sin respuesta del servidor
        messageApi.error('Error de conexión al servidor');
      } else {
        // Otro tipo de error
        messageApi.error(`Error: ${error.message || 'Error desconocido'}`);
      }
    } finally {
      setLoading(false);
    }
  };

  const handleCancelar = () => {
    Modal.confirm({
      title: '¿Está seguro de cancelar?',
      content: 'Se perderán todos los datos no guardados',
      okText: 'Sí',
      cancelText: 'No',
      onOk: () => {
        // Limpiar todos los estados
        form.resetFields();
        setRegistros([]);
        setTotalChecked(0);
        setResumenes([]);
        setRegistrosParaGrabar([]);
        setCamposBloqueados(false);
        setCabeceraFija(false);
        setValorCLO(0);
        setFardosEnLote(0);
        setLoteEnModificacion(null);
        setDesdeMuestraValue(null);
        setHastaMuestraValue(null);
        setSelectedResumen(null);
        setOrigenSelected(false);
        setClienteSelected(false);
        setSelectedOrigen(null);
        setSelectedCliente(null);

        messageApi.success('Todos los datos han sido limpiados');
      }
    });
  };

  // Modificar los manejadores - Ambos campos son independientes
  const handleOrigenChange = (value: number | null) => {
    console.log('handleOrigenChange', value)
    if (!value) {  // Si el valor es null (selección anulada)
      setOrigenSelected(false);
      form.setFieldValue('origen_id', undefined);
    } else {
      setOrigenSelected(true);
    }
  };

  const handleClienteChange = (value: number | null) => {
    console.log('handleClienteChange', value)
    if (!value && value !== 0) {  // Si el valor es null (selección anulada)
      console.log('paso por aqui-no-')
      setClienteSelected(false);
      form.setFieldValue('cliente_id', undefined);
    } else {
      console.log('paso por aqui-si-')
      setClienteSelected(true);
    }
  };

  // Modificar los manejadores para incluir el evento de tecla
  const handleOrigenKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Escape') {
      form.setFieldValue('origen_id', undefined);
      setOrigenSelected(false);
    }
  };

  const handleClienteKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Escape') {
      form.setFieldValue('cliente_id', undefined);
      setClienteSelected(false);
    }
  };

  // Agregar el manejador de checkbox
  const handleCheckboxChange = (index: number, checked: boolean) => {
    setRegistros(prev => {
      const newRegistros = [...prev];
      newRegistros[index].checked = checked;
      return newRegistros;
    });
    
    setTotalChecked(prev => checked ? prev + 1 : prev - 1);
  };

  const handleResumenSelect = (resumenId: number) => {
    // Limpiar la selección anterior si se hace clic en el mismo resumen
    if (selectedResumen === resumenId) {
        setSelectedResumen(null);
        setCanModify(false);
        return;
    }
    
    // Establecer el nuevo resumen seleccionado
    setSelectedResumen(resumenId);
    setCanModify(true);
  };

  // Agregar esta nueva función para actualizar los campos del formulario
  const actualizarCamposFormulario = (registros: FardoRecord[]) => {
    if (registros.length === 0) return;

    // Obtener el primer registro para extraer la calidad
    const primerRegistro = registros[0];
    const calidadMatch = primerRegistro.calidad.match(/([A-F])((?:-[1-3]\/4)?)/);
    
    if (calidadMatch) {
        const [, letra, fraccion] = calidadMatch;
        form.setFieldsValue({
            calidad_letra: letra as CalidadLetra,
            calidad_fraccion: (fraccion || '') as CalidadFraccion,
            micronaire: primerRegistro.micronaire,
            longitud_hebra: primerRegistro.longitud,
            resistencia: primerRegistro.resistencia
        });
    }

    // Extraer origen, cliente y números de muestra
    const [origen, cliente, desdeMuestra] = primerRegistro.nroMuestra.split('-');
    const [,, hastaMuestra] = registros[registros.length - 1].nroMuestra.split('-');

    // Actualizar los campos de origen y cliente - Ambos pueden tener valores
    form.setFieldsValue({
        origen_id: origen === '0' ? undefined : Number(origen),
        cliente_id: cliente === '0' ? 0 : Number(cliente)
    });
    setOrigenSelected(origen !== '0');
    setClienteSelected(cliente !== '0');

    // Actualizar el campo de lote usando la propiedad lote del registro
    form.setFieldsValue({
        lote: primerRegistro.lote
    });

    // Actualizar los campos de muestra
    form.setFieldsValue({
        desde_muestra: Number(desdeMuestra),
        hasta_muestra: Number(hastaMuestra)
    });

    // Actualizar los estados de los valores de muestra
    setDesdeMuestraValue(Number(desdeMuestra));
    setHastaMuestraValue(Number(hastaMuestra));
  };

  // Modificar el handleModificar para incluir la actualización de campos
  const handleModificar = () => {
    if (selectedResumen === null) {
        messageApi.warning('Debe seleccionar un resumen para modificar');
        return;
    }

    Modal.confirm({
        title: '¿Está seguro de modificar este registro?',
        content: 'Los registros seleccionados volverán al detalle para su modificación',
        okText: 'Sí',
        cancelText: 'No',
        onOk: () => {
            // Encontrar el resumen seleccionado y sus registros correspondientes
            const resumenIndex = resumenes.findIndex(r => r.id === selectedResumen);
            if (resumenIndex === -1) return;

            // Obtener los registros asociados a este resumen
            const registrosDelResumen = registrosParaGrabar.slice(
                resumenIndex > 0 ? resumenes.slice(0, resumenIndex).reduce((acc, r) => acc + r.cantidadSeleccionada, 0) : 0,
                resumenes.slice(0, resumenIndex + 1).reduce((acc, r) => acc + r.cantidadSeleccionada, 0)
            );

            // Restaurar los registros al detalle y marcarlos como seleccionados
            const registrosRestaurados = registrosDelResumen.map(reg => ({
                ...reg,
                checked: true
            }));

            // Actualizar los campos del formulario con los valores del resumen
            actualizarCamposFormulario(registrosRestaurados);

            // Actualizar los registros y estados
            setRegistros(registrosRestaurados);
            setTotalChecked(registrosRestaurados.length);

            // Eliminar el resumen seleccionado
            setResumenes(prev => prev.filter(r => r.id !== selectedResumen));

            // Eliminar los registros del array para grabar
            const nuevosRegistrosParaGrabar = [...registrosParaGrabar];
            nuevosRegistrosParaGrabar.splice(
                resumenIndex > 0 ? resumenes.slice(0, resumenIndex).reduce((acc, r) => acc + r.cantidadSeleccionada, 0) : 0,
                registrosDelResumen.length
            );
            setRegistrosParaGrabar(nuevosRegistrosParaGrabar);

            // Bloquear Lote y Calidad; Desde/Hasta quedan libres para agregar más rangos
            const loteDelResumen = registrosDelResumen[0]?.lote ?? null;
            setCamposBloqueados(true);
            setLoteEnModificacion(loteDelResumen);
            setSelectedResumen(null);
            setCanModify(false);

            messageApi.info(`Editando Lote ${loteDelResumen}. Modificá los fardos y hacé Resumir para confirmar.`, 5);
        }
    });
  };

  const handleResumen = () => {
    if (registros.length === 0) {
        messageApi.warning('No hay registros para resumir');
        return;
    }

    console.log('=== INICIO RESUMEN ===');
    console.log('Registros a procesar:', registros);

    const muestrasChecked = registros.filter(r => r.checked);
    console.log('Muestras seleccionadas:', muestrasChecked);

    // Validar límite CLO: DB + sesión previa del mismo lote + selección actual
    if (valorCLO > 0 && muestrasChecked.length > 0) {
      const currentLote = muestrasChecked[0].lote;
      const fardosSessionMismoLote = registrosParaGrabar.filter(r => r.lote === currentLote).length;
      const total = muestrasChecked.length + fardosEnLote + fardosSessionMismoLote;
      if (total > valorCLO) {
        messageApi.error(
          `Lote ${currentLote}: selección (${muestrasChecked.length}) + en DB (${fardosEnLote}) + ya en planilla (${fardosSessionMismoLote}) = ${total} supera el límite CLO de ${valorCLO}. Deseleccioná ${total - valorCLO} fardo/s.`
        );
        return;
      }
    }

    // Agrupar por lote usando la propiedad lote del registro
    const gruposPorLote = muestrasChecked.reduce((grupos, muestra) => {
        const [origen, cliente, nro] = muestra.nroMuestra.split('-');
        const key = `${origen}-${cliente}-${muestra.lote}`;
        
        // Debug: Imprimir información de cada muestra
        console.log('Muestra procesada:', {
            nroMuestra: muestra.nroMuestra,
            origen,
            cliente,
            nro,
            lote: muestra.lote,
            key
        });
        
        if (!grupos[key]) {
            grupos[key] = [];
        }
        grupos[key].push(muestra);
        return grupos;
    }, {} as Record<string, FardoRecord[]>);

    console.log('Grupos por lote:', gruposPorLote);

    // Crear resúmenes para cada grupo
    Object.entries(gruposPorLote).forEach(([key, muestrasGrupo]) => {
        const [origen, cliente, lote] = key.split('-');
        console.log(`\nProcesando grupo ${key}:`);
        console.log('Muestras en este grupo:', muestrasGrupo);
        
        const todosLosNumeros = muestrasGrupo.map(f => {
            const [,,nro] = f.nroMuestra.split('-');
            return Number(nro);
        });
        
        const minMuestra = Math.min(...todosLosNumeros);
        const maxMuestra = Math.max(...todosLosNumeros);
        
        // Calcular promedios para este grupo
        const promedios = muestrasGrupo.reduce((acc, curr) => ({
            micronaire: acc.micronaire + (Number(curr.micronaire) || 0),
            longitud: acc.longitud + (Number(curr.longitud) || 0),
            resistencia: acc.resistencia + (Number(curr.resistencia) || 0)
        }), { micronaire: 0, longitud: 0, resistencia: 0 });

        const cantidad = muestrasGrupo.length;
        const micPromed = (promedios.micronaire / cantidad).toFixed(2);
        const longPromed = (promedios.longitud / cantidad).toFixed(2);
        const resisPromed = (promedios.resistencia / cantidad).toFixed(2);

        // Crear resumen incluyendo el lote
        const resumenTexto = `${minMuestra}/${maxMuestra} (${cantidad}) ${origen}-${cliente} Lote:${lote} ${muestrasGrupo[0].calidad} ${micPromed} ${longPromed} ${resisPromed}`;

        const nuevoResumen: ResumenRecord = {
            id: resumenes.length,
            texto: resumenTexto,
            cantidadSeleccionada: cantidad,
            totalRegistros: registros.length,
            calidadNumerica: muestrasGrupo[0].calidadNumerica,
            lote: Number(lote)
        };

        console.log('Nuevo resumen generado:', nuevoResumen);

        setResumenes(prev => {
            const updated = [...prev, nuevoResumen];
            console.log('Array de resúmenes actualizado:', updated);
            return updated;
        });

        setRegistrosParaGrabar(prev => {
            const updated = [...prev, ...muestrasGrupo];
            console.log('Array de registros para grabar actualizado:', updated);
            return updated;
        });
    });

    console.log('=== FIN RESUMEN ===');

    setRegistros([]);
    setTotalChecked(0);
    setCamposBloqueados(false);
    setLoteEnModificacion(null);
    // cabeceraFija permanece true hasta grabar/cancelar
    form.setFieldsValue({ desde_muestra: null, hasta_muestra: null });

    messageApi.success('Resumen generado. Podés buscar otro rango o grabar todo.');
  };

  // Observar cambios en el formulario para actualizar el progreso
  const formValues = Form.useWatch([], form);

  // Calcular progreso del formulario
  const formProgress = useMemo(() => {
    let completed = 0;
    const total = 10;

    if (formValues?.fecha) completed++;
    if (formValues?.nro_planilla) completed++;
    if (formValues?.origen_id || formValues?.cliente_id !== undefined) completed++;
    if (formValues?.lote) completed++;
    if (formValues?.calidad_letra) completed++;
    if (formValues?.micronaire) completed++;
    if (formValues?.longitud_hebra) completed++;
    if (formValues?.resistencia) completed++;
    if (formValues?.desde_muestra) completed++;
    if (formValues?.hasta_muestra) completed++;

    return Math.round((completed / total) * 100);
  }, [formValues]);

  // Calcular índices de calidad y estados HVI
  const hviAnalysis = useMemo(() => {
    if (!formValues?.micronaire || !formValues?.longitud_hebra || !formValues?.resistencia) {
      return null;
    }

    const micronaireStatus = evaluateHVIParameter('micronaire', formValues.micronaire);
    const longitudStatus = evaluateHVIParameter('longitud', formValues.longitud_hebra);
    const resistenciaStatus = evaluateHVIParameter('resistencia', formValues.resistencia);

    const fqi = calculateFQI(formValues.longitud_hebra, formValues.resistencia, 82, formValues.micronaire);
    const sci = calculateSCI(formValues.longitud_hebra, formValues.resistencia, formValues.micronaire);

    return {
      micronaire: { value: formValues.micronaire, status: micronaireStatus },
      longitud: { value: formValues.longitud_hebra, status: longitudStatus },
      resistencia: { value: formValues.resistencia, status: resistenciaStatus },
      fqi,
      sci
    };
  }, [formValues]);

  // Calcular estadísticas mejoradas de los registros
  const estadisticasRegistros = useMemo(() => {
    if (registros.length === 0) return null;

    const micronaireValues = registros.map(r => r.micronaire);
    const longitudValues = registros.map(r => r.longitud);
    const resistenciaValues = registros.map(r => r.resistencia);

    return {
      micronaire: {
        mean: calculateMean(micronaireValues),
        median: calculateMedian(micronaireValues),
        stdDev: calculateStdDev(micronaireValues),
        cv: calculateCV(micronaireValues),
        minMax: calculateMinMax(micronaireValues),
        optimalPercentage: calculateOptimalPercentage(micronaireValues, 'micronaire')
      },
      longitud: {
        mean: calculateMean(longitudValues),
        median: calculateMedian(longitudValues),
        stdDev: calculateStdDev(longitudValues),
        cv: calculateCV(longitudValues),
        minMax: calculateMinMax(longitudValues),
        optimalPercentage: calculateOptimalPercentage(longitudValues, 'longitud')
      },
      resistencia: {
        mean: calculateMean(resistenciaValues),
        median: calculateMedian(resistenciaValues),
        stdDev: calculateStdDev(resistenciaValues),
        cv: calculateCV(resistenciaValues),
        minMax: calculateMinMax(resistenciaValues),
        optimalPercentage: calculateOptimalPercentage(resistenciaValues, 'resistencia')
      },
      totalMuestras: registros.length,
      muestrasSeleccionadas: totalChecked
    };
  }, [registros, totalChecked]);

  // Agregar función para desbloquear campos cuando se modifica
  const desbloquearCampos = () => {
    setCamposBloqueados(false);
    form.setFieldsValue({
      desde_muestra: null,
      hasta_muestra: null
    });
  };

  return (
    <App>
      <div style={{ padding: '0' }}>
        {/* Breadcrumbs */}
        <Breadcrumb
          style={{ marginBottom: '4px' }}
          items={[
            {
              href: '/',
              title: <><HomeOutlined /> Inicio</>
            },
            {
              title: <><FileTextOutlined /> Clasificación</>
            },
            {
              title: 'Clasificación [FIBRA]'
            }
          ]}
        />

        {/* Título, progreso y panel HVI */}
        <div style={{ 
          display: 'flex', 
          justifyContent: 'space-between', 
          alignItems: 'center',
          marginBottom: '6px',
          flexWrap: 'wrap',
          gap: '12px'
        }}>
          <h1 style={{ 
            color: theme.colors.primaryDark, 
            fontSize: '24px', 
            fontWeight: 600, 
            margin: 0
          }}>
            Clasificación <span style={{ color: theme.colors.textSecondary, fontSize: '18px' }}>[FIBRA]</span>
          </h1>

          <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
            {/* Indicador de progreso */}
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              <Text type="secondary" style={{ fontSize: '12px' }}>
                Progreso:
              </Text>
              <Progress 
                percent={formProgress} 
                size="small" 
                style={{ width: '120px', margin: 0 }}
                strokeColor={formProgress === 100 ? theme.colors.success : theme.colors.primary}
                showInfo={true}
              />
            </div>

            {/* Panel de Análisis HVI e Índices de Calidad */}
            {hviAnalysis && (
              <div style={{ 
                display: 'flex', 
                alignItems: 'center', 
                gap: '16px',
                paddingLeft: '16px',
                borderLeft: `1px solid ${theme.colors.borderLight}`
              }}>
                <div style={{ textAlign: 'center' }}>
                  <Text type="secondary" style={{ fontSize: '10px', display: 'block', marginBottom: '2px' }}>
                    FQI
                  </Text>
                  <Text strong style={{ fontSize: '14px', color: theme.colors.primary }}>
                    {hviAnalysis.fqi.toFixed(2)}
                  </Text>
                </div>
                <div style={{ textAlign: 'center' }}>
                  <Text type="secondary" style={{ fontSize: '10px', display: 'block', marginBottom: '2px' }}>
                    SCI
                  </Text>
                  <Text strong style={{ fontSize: '14px', color: theme.colors.secondary }}>
                    {hviAnalysis.sci.toFixed(2)}
                  </Text>
                </div>
                <div style={{ textAlign: 'center' }}>
                  <Text type="secondary" style={{ fontSize: '10px', display: 'block', marginBottom: '2px' }}>
                    Estado
                  </Text>
                  <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', gap: '2px' }}>
                    {hviAnalysis.micronaire.status === 'optimal' && 
                     hviAnalysis.longitud.status === 'optimal' && 
                     hviAnalysis.resistencia.status === 'optimal' ? (
                      <>
                        <CheckCircleOutlined style={{ color: theme.colors.success, fontSize: '14px' }} />
                        <Text strong style={{ color: theme.colors.success, fontSize: '12px' }}>Óptimo</Text>
                      </>
                    ) : (
                      <>
                        <WarningOutlined style={{ color: theme.colors.warning, fontSize: '14px' }} />
                        <Text strong style={{ color: theme.colors.warning, fontSize: '12px' }}>Revisar</Text>
                      </>
                    )}
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>

        <style>{`
          .clasificacion-buttons .ant-btn-primary {
            color: #ffffff !important;
            font-size: 12px !important;
          }
          .clasificacion-buttons .ant-btn:not(.ant-btn-primary) {
            color: ${theme.colors.textPrimary} !important;
            font-size: 12px !important;
          }
        `}</style>
        <Card
          style={{ 
            backgroundColor: '#ffffff',
            borderRadius: theme.styles.borderRadius,
            boxShadow: theme.styles.boxShadow,
            maxWidth: '100%'
          }}
          styles={{
            header: {
              backgroundColor: theme.colors.primaryDark,
              color: 'white',
              borderRadius: '8px 8px 0 0',
              fontSize: '16px',
              fontWeight: 600,
              padding: '8px 16px'
            },
            body: {
              padding: '12px 16px'
            }
          }}
        >
          <Form
            form={form}
            layout="vertical"
            initialValues={{
              fecha: moment(),
            }}
            onFinish={handleSubmit}
            className="clasificacion-form"
          >
            <style>{`
              .clasificacion-form .ant-form-item-label > label {
                color: ${theme.colors.textPrimary} !important;
                font-weight: 500 !important;
                font-size: 11px !important;
                line-height: 1.3 !important;
              }
              .clasificacion-form .ant-form-item {
                margin-bottom: 8px !important;
              }
              .clasificacion-form .ant-form-item-label {
                padding-bottom: 2px !important;
              }
              .clasificacion-form .ant-form-item-explain {
                margin-top: 2px !important;
              }
              .clasificacion-form .ant-form-item-required::before {
                color: ${theme.colors.error} !important;
              }
              .clasificacion-form .ant-input,
              .clasificacion-form .ant-select-selector,
              .clasificacion-form .ant-input-number,
              .clasificacion-form .ant-picker {
                border-color: ${theme.colors.border} !important;
                border-radius: 6px !important;
                font-size: 12px !important;
                padding: 1px 6px !important;
                height: 26px !important;
              }
              .clasificacion-form .ant-input-number-input {
                font-size: 12px !important;
                padding: 0 2px !important;
              }
              .clasificacion-form .ant-select-selection-item {
                font-size: 12px !important;
                line-height: 24px !important;
              }
              .clasificacion-form .ant-select-selector {
                padding: 0 6px !important;
              }
              .clasificacion-form .ant-input:focus,
              .clasificacion-form .ant-select-selector:focus,
              .clasificacion-form .ant-input-number:focus,
              .clasificacion-form .ant-picker:focus {
                border-color: ${theme.colors.primary} !important;
                box-shadow: 0 0 0 2px rgba(45, 92, 146, 0.2) !important;
              }
              .clasificacion-form .ant-btn-primary {
                color: #ffffff !important;
                font-size: 12px !important;
              }
              .clasificacion-form .ant-btn:not(.ant-btn-primary) {
                color: ${theme.colors.textPrimary} !important;
                font-size: 12px !important;
              }
            `}</style>

            {/* Sección: Datos Básicos */}
            <Row gutter={[10, 8]}>
                <Col span={6}>
                  <Form.Item
                    label={<span style={{ color: theme.colors.textPrimary, fontWeight: 500 }}>Fecha</span>}
                    name="fecha"
                    rules={[{ required: true, message: 'La fecha es obligatoria' }]}
                  >
                    <DatePicker 
                      style={{ width: '100%' }}
                      format="DD/MM/YYYY"
                      disabled={cabeceraFija}
                      placeholder="Ej: 15/01/2024"
                      size="small"
                    />
                  </Form.Item>
                </Col>
                <Col span={6}>
                  <Form.Item
                    label={<span style={{ color: theme.colors.textPrimary, fontWeight: 500 }}>N° Planilla</span>}
                    name="nro_planilla"
                    rules={[{ required: true, message: 'El número de planilla es obligatorio' }]}
                  >
                    <InputNumber 
                      style={{ width: '100%' }}
                      min={1}
                      disabled={cabeceraFija}
                      placeholder="Ej: 12345"
                      size="small"
                    />
                  </Form.Item>
                </Col>
                <Col span={6}>
                  <Form.Item 
                    label={<span style={{ color: theme.colors.textPrimary, fontWeight: 500 }}>Origen</span>}
                    name="origen_id" 
                    rules={[{ 
                      required: true,
                      message: 'El origen es obligatorio'
                    }]}
                  >
                    <div style={{ position: 'relative' }} className="search-dropdown">
                      <Input
                        placeholder="Buscar por NC o denominación..."
                        disabled={cabeceraFija}
                        prefix={<SearchOutlined style={{ color: theme.colors.textSecondary }} />}
                        value={selectedOrigen ? `${selectedOrigen.nc} - ${selectedOrigen.denominacion}` : searchOrigen}
                        onChange={e => {
                          setSearchOrigen(e.target.value);
                          setSelectedOrigen(null);
                          setShowSearchOrigen(true);
                          form.setFieldsValue({ origen_id: undefined });
                        }}
                        onFocus={() => !cabeceraFija && setShowSearchOrigen(true)}
                        allowClear={!cabeceraFija}
                        onClear={() => {
                          setSelectedOrigen(null);
                          setSearchOrigen('');
                          form.setFieldsValue({ origen_id: undefined });
                          handleOrigenChange(null);
                        }}
                        size="small"
                        style={{
                          backgroundColor: selectedOrigen || cabeceraFija ? theme.colors.backgroundGray : 'white'
                        }}
                      />
                      {/* Dropdown */}
                      {showSearchOrigen && padrones.length > 0 && !selectedOrigen && !cabeceraFija && (
                        <div style={{
                          position: 'absolute',
                          top: '100%',
                          left: 0,
                          right: 0,
                          backgroundColor: 'white',
                          border: `1px solid ${theme.colors.borderLight}`,
                          borderRadius: theme.styles.borderRadius,
                          maxHeight: '200px',
                          overflowY: 'auto',
                          zIndex: 1000,
                          boxShadow: theme.styles.boxShadow,
                          marginTop: '4px'
                        }}>
                          {padrones.filter(padron => {
                            const search = searchOrigen.trim().toLowerCase();
                            return (
                              padron.nc.toLowerCase().includes(search) ||
                              padron.denominacion.toLowerCase().includes(search)
                            );
                          }).map(padron => (
                            <div
                              key={padron.id}
                              style={{
                                padding: '8px 12px',
                                cursor: 'pointer',
                                borderBottom: `1px solid ${theme.colors.borderLight}`
                              }}
                              onMouseEnter={e => (e.currentTarget.style.backgroundColor = theme.colors.backgroundGray)}
                              onMouseLeave={e => (e.currentTarget.style.backgroundColor = 'white')}
                              onClick={() => {
                                setSelectedOrigen(padron);
                                setSearchOrigen('');
                                setShowSearchOrigen(false);
                                form.setFieldsValue({ origen_id: padron.id });
                                handleOrigenChange(padron.id);
                              }}
                            >
                              <Text strong>{padron.nc}</Text> - {padron.denominacion}
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  </Form.Item>
                </Col>
                <Col span={6}>
                  <Form.Item 
                    label={<span style={{ color: theme.colors.textPrimary, fontWeight: 500 }}>Cliente</span>}
                    name="cliente_id"
                    rules={[{ 
                      required: true,
                      message: 'El cliente es obligatorio'
                    }]}
                  >
                    <div style={{ position: 'relative' }} className="search-dropdown">
                      <Input
                        placeholder="Buscar por NC o denominación..."
                        disabled={cabeceraFija}
                        prefix={<SearchOutlined style={{ color: theme.colors.textSecondary }} />}
                        value={selectedCliente ? `${selectedCliente.nc} - ${selectedCliente.denominacion}` : searchCliente}
                        onChange={e => {
                          setSearchCliente(e.target.value);
                          setSelectedCliente(null);
                          setShowSearchCliente(true);
                          form.setFieldsValue({ cliente_id: undefined });
                        }}
                        onFocus={() => !cabeceraFija && setShowSearchCliente(true)}
                        allowClear={!cabeceraFija}
                        onClear={() => {
                          setSelectedCliente(null);
                          setSearchCliente('');
                          form.setFieldsValue({ cliente_id: undefined });
                          handleClienteChange(null);
                        }}
                        size="small"
                        style={{
                          backgroundColor: selectedCliente || cabeceraFija ? theme.colors.backgroundGray : 'white'
                        }}
                      />
                      {/* Dropdown */}
                      {showSearchCliente && padrones.length > 0 && !selectedCliente && !cabeceraFija && (
                        <div style={{
                          position: 'absolute',
                          top: '100%',
                          left: 0,
                          right: 0,
                          backgroundColor: 'white',
                          border: `1px solid ${theme.colors.borderLight}`,
                          borderRadius: theme.styles.borderRadius,
                          maxHeight: '200px',
                          overflowY: 'auto',
                          zIndex: 1000,
                          boxShadow: theme.styles.boxShadow,
                          marginTop: '4px'
                        }}>
                          {padrones.filter(padron => {
                            const search = searchCliente.trim().toLowerCase();
                            return (
                              padron.nc.toLowerCase().includes(search) ||
                              padron.denominacion.toLowerCase().includes(search)
                            );
                          }).map(padron => (
                            <div
                              key={padron.id}
                              style={{
                                padding: '8px 12px',
                                cursor: 'pointer',
                                borderBottom: `1px solid ${theme.colors.borderLight}`
                              }}
                              onMouseEnter={e => (e.currentTarget.style.backgroundColor = theme.colors.backgroundGray)}
                              onMouseLeave={e => (e.currentTarget.style.backgroundColor = 'white')}
                              onClick={() => {
                                setSelectedCliente(padron);
                                setSearchCliente('');
                                setShowSearchCliente(false);
                                form.setFieldsValue({ cliente_id: padron.id });
                                handleClienteChange(padron.id);
                              }}
                            >
                              <Text strong>{padron.nc}</Text> - {padron.denominacion}
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  </Form.Item>
                </Col>
              </Row>

            {/* Sección: Parámetros de Clasificación y Rango de Muestras */}
            <Row gutter={[10, 8]} style={{ marginTop: '8px', alignItems: 'flex-end' }}>
              {/* Calidad */}
              <Col span={3}>
                <Form.Item 
                  label={<span style={{ color: theme.colors.textPrimary, fontWeight: 500, fontSize: '11px' }}>Calidad</span>}
                  style={{ marginBottom: 0 }}
                >
                  <Space style={{ display: 'flex', gap: '2px' }}>
                    <Form.Item
                      name="calidad_letra"
                      noStyle
                      rules={[{
                        required: true,
                        message: 'La letra de calidad es obligatoria'
                      }]}
                    >
                      <Select style={{ width: '60px' }} placeholder="Letra" size="small" disabled={camposBloqueados}>
                        {['A', 'B', 'C', 'D', 'E', 'F'].map(letra => (
                          <Option key={letra} value={letra}>{letra}</Option>
                        ))}
                      </Select>
                    </Form.Item>
                    <Form.Item name="calidad_fraccion" noStyle>
                      <Select style={{ width: '60px' }} placeholder="Fracción" size="small" disabled={camposBloqueados}>
                        <Option value="">-</Option>
                        <Option value="-1/4">-1/4</Option>
                        <Option value="-1/2">-1/2</Option>
                        <Option value="-3/4">-3/4</Option>
                      </Select>
                    </Form.Item>
                  </Space>
                </Form.Item>
              </Col>
              
              {/* Lote */}
              <Col span={3}>
                <Form.Item 
                  label={<span style={{ color: theme.colors.textPrimary, fontWeight: 500, fontSize: '11px' }}>Lote</span>}
                  name="lote" 
                  rules={[{ required: true, message: 'El lote es obligatorio' }]}
                  style={{ marginBottom: 0 }}
                >
                  <InputNumber
                    style={{ width: '100%' }}
                    min={1}
                    placeholder="Ej: 1"
                    size="small"
                    disabled={camposBloqueados}
                  />
                </Form.Item>
              </Col>

              {/* Micronaire */}
              <Col span={3}>
                <Form.Item 
                  label={
                    <div style={{ display: 'flex', alignItems: 'center', gap: '3px' }}>
                      <span style={{ color: theme.colors.textPrimary, fontWeight: 500, fontSize: '11px' }}>Micronaire</span>
                      {hviAnalysis && (
                        <Tooltip title={`Rango óptimo: ${HVI_RANGES.micronaire.optimal.min}-${HVI_RANGES.micronaire.optimal.max} ${HVI_RANGES.micronaire.unit}`}>
                          {getStatusIcon(hviAnalysis.micronaire.status)}
                        </Tooltip>
                      )}
                    </div>
                  }
                  name="micronaire"
                  rules={[
                    { required: true, message: 'El micronaire es obligatorio' },
                    { 
                      type: 'number', 
                      min: HVI_RANGES.micronaire.min, 
                      max: HVI_RANGES.micronaire.max,
                      message: `El micronaire debe estar entre ${HVI_RANGES.micronaire.min} y ${HVI_RANGES.micronaire.max}`
                    }
                  ]}
                  help={
                    hviAnalysis && (hviAnalysis.micronaire.status === 'warning' || hviAnalysis.micronaire.status === 'error') ? (
                      <div>
                        {hviAnalysis.micronaire.status === 'warning' && (
                          <Alert
                            message="Valor fuera del rango óptimo"
                            type="warning"
                            showIcon
                            style={{ marginTop: '4px', fontSize: '11px', padding: '4px 8px' }}
                          />
                        )}
                        {hviAnalysis.micronaire.status === 'error' && (
                          <Alert
                            message="Valor fuera del rango aceptable"
                            type="error"
                            showIcon
                            style={{ marginTop: '4px', fontSize: '11px', padding: '4px 8px' }}
                          />
                        )}
                      </div>
                    ) : null
                  }
                  style={{ marginBottom: 0 }}
                >
                  <InputNumber 
                    style={{ 
                      width: '100%',
                      borderColor: hviAnalysis ? getStatusColor(hviAnalysis.micronaire.status) : undefined
                    }} 
                    min={0}
                    step={0.01}
                    placeholder="Ej: 4.5"
                    size="small"
                  />
                </Form.Item>
              </Col>

              {/* Longitud Hebra */}
              <Col span={3}>
                <Form.Item 
                  label={
                    <div style={{ display: 'flex', alignItems: 'center', gap: '3px' }}>
                      <span style={{ color: theme.colors.textPrimary, fontWeight: 500, fontSize: '11px' }}>Longitud (mm)</span>
                      {hviAnalysis && (
                        <Tooltip title={`Rango óptimo: ${HVI_RANGES.longitud.optimal.min}-${HVI_RANGES.longitud.optimal.max} ${HVI_RANGES.longitud.unit}`}>
                          {getStatusIcon(hviAnalysis.longitud.status)}
                        </Tooltip>
                      )}
                    </div>
                  }
                  name="longitud_hebra"
                  rules={[
                    { required: true, message: 'La longitud es obligatoria' },
                    { 
                      type: 'number', 
                      min: HVI_RANGES.longitud.min, 
                      max: HVI_RANGES.longitud.max,
                      message: `La longitud debe estar entre ${HVI_RANGES.longitud.min} y ${HVI_RANGES.longitud.max} mm`
                    }
                  ]}
                  help={
                    hviAnalysis && (hviAnalysis.longitud.status === 'warning' || hviAnalysis.longitud.status === 'error') ? (
                      <div>
                        {hviAnalysis.longitud.status === 'warning' && (
                          <Alert
                            message="Valor fuera del rango óptimo"
                            type="warning"
                            showIcon
                            style={{ marginTop: '4px', fontSize: '11px', padding: '4px 8px' }}
                          />
                        )}
                        {hviAnalysis.longitud.status === 'error' && (
                          <Alert
                            message="Valor fuera del rango aceptable"
                            type="error"
                            showIcon
                            style={{ marginTop: '4px', fontSize: '11px', padding: '4px 8px' }}
                          />
                        )}
                      </div>
                    ) : null
                  }
                  style={{ marginBottom: 0 }}
                >
                  <InputNumber 
                    style={{ 
                      width: '100%',
                      borderColor: hviAnalysis ? getStatusColor(hviAnalysis.longitud.status) : undefined
                    }} 
                    min={0}
                    step={0.01}
                    placeholder="Ej: 28.5"
                    size="small"
                  />
                </Form.Item>
              </Col>

              {/* Resistencia */}
              <Col span={3}>
                <Form.Item 
                  label={
                    <div style={{ display: 'flex', alignItems: 'center', gap: '3px' }}>
                      <span style={{ color: theme.colors.textPrimary, fontWeight: 500, fontSize: '11px' }}>Resistencia</span>
                      {hviAnalysis && (
                        <Tooltip title={`Rango óptimo: ${HVI_RANGES.resistencia.optimal.min}-${HVI_RANGES.resistencia.optimal.max} ${HVI_RANGES.resistencia.unit}`}>
                          {getStatusIcon(hviAnalysis.resistencia.status)}
                        </Tooltip>
                      )}
                    </div>
                  }
                  name="resistencia"
                  rules={[
                    { required: true, message: 'La resistencia es obligatoria' },
                    { 
                      type: 'number', 
                      min: HVI_RANGES.resistencia.min, 
                      max: HVI_RANGES.resistencia.max,
                      message: `La resistencia debe estar entre ${HVI_RANGES.resistencia.min} y ${HVI_RANGES.resistencia.max} g/tex`
                    }
                  ]}
                  help={
                    hviAnalysis && (hviAnalysis.resistencia.status === 'warning' || hviAnalysis.resistencia.status === 'error') ? (
                      <div>
                        {hviAnalysis.resistencia.status === 'warning' && (
                          <Alert
                            message="Valor fuera del rango óptimo"
                            type="warning"
                            showIcon
                            style={{ marginTop: '4px', fontSize: '11px', padding: '4px 8px' }}
                          />
                        )}
                        {hviAnalysis.resistencia.status === 'error' && (
                          <Alert
                            message="Valor fuera del rango aceptable"
                            type="error"
                            showIcon
                            style={{ marginTop: '4px', fontSize: '11px', padding: '4px 8px' }}
                          />
                        )}
                      </div>
                    ) : null
                  }
                  style={{ marginBottom: 0 }}
                >
                  <InputNumber 
                    style={{ 
                      width: '100%',
                      borderColor: hviAnalysis ? getStatusColor(hviAnalysis.resistencia.status) : undefined
                    }} 
                    min={0}
                    step={0.01}
                    placeholder="Ej: 30.2"
                    size="small"
                  />
                </Form.Item>
              </Col>

              {/* Desde Muestra */}
              <Col span={3}>
                <Form.Item
                  label={<span style={{ color: theme.colors.textPrimary, fontWeight: 500, fontSize: '11px' }}>Desde</span>}
                  name="desde_muestra"
                  rules={[{ required: true, message: 'El rango inicial es obligatorio' }]}
                  style={{ marginBottom: 0 }}
                >
                  <InputNumber
                    style={{ width: '100%' }}
                    min={1}
                    placeholder="Ej: 1"
                    size="small"
                  />
                </Form.Item>
              </Col>

              {/* Hasta Muestra */}
              <Col span={3}>
                <Form.Item
                  label={<span style={{ color: theme.colors.textPrimary, fontWeight: 500, fontSize: '11px' }}>Hasta</span>}
                  name="hasta_muestra"
                  rules={[{ required: true, message: 'El rango final es obligatorio' }]}
                  style={{ marginBottom: 0 }}
                >
                  <InputNumber
                    style={{ width: '100%' }}
                    min={1}
                    placeholder="Ej: 10"
                    size="small"
                  />
                </Form.Item>
              </Col>

              {/* Botón Generar Registros */}
              <Col span={3}>
                <Form.Item label=" " style={{ marginBottom: 0 }}>
                  <Button
                    type="primary"
                    htmlType="submit"
                    loading={enviando}
                    size="small"
                    style={{ 
                      width: '100%',
                      backgroundColor: theme.colors.secondary,
                      borderColor: theme.colors.secondary,
                      fontWeight: 500,
                      whiteSpace: 'nowrap',
                      padding: '4px 12px',
                      fontSize: '12px'
                    }}
                  >
                    {enviando ? 'Generando...' : 'Generar Registros'}
                  </Button>
                </Form.Item>
              </Col>
            </Row>

          </Form>

          {/* Dashboard de Estadísticas */}
          {estadisticasRegistros && (
            <div style={{ marginTop: '8px' }}>
              <Row gutter={[16, 16]}>
                {/* KPI Cards */}
                <Col span={6}>
                  <Card size="small" style={{ textAlign: 'center', backgroundColor: theme.colors.infoLight }}>
                    <Text type="secondary" style={{ fontSize: '12px' }}>Total Muestras</Text>
                    <div style={{ fontSize: '24px', fontWeight: 600, color: theme.colors.primary, marginTop: '8px' }}>
                      {estadisticasRegistros.totalMuestras}
                    </div>
                  </Card>
                </Col>
                <Col span={6}>
                  <Card size="small" style={{ textAlign: 'center', backgroundColor: theme.colors.successLight }}>
                    <Text type="secondary" style={{ fontSize: '12px' }}>Seleccionadas</Text>
                    <div style={{ fontSize: '24px', fontWeight: 600, color: theme.colors.success, marginTop: '8px' }}>
                      {estadisticasRegistros.muestrasSeleccionadas}
                    </div>
                  </Card>
                </Col>
                <Col span={6}>
                  <Card size="small" style={{ textAlign: 'center', backgroundColor: theme.colors.warningLight }}>
                    <Text type="secondary" style={{ fontSize: '12px' }}>% Óptimo Micronaire</Text>
                    <div style={{ fontSize: '24px', fontWeight: 600, color: theme.colors.warning, marginTop: '8px' }}>
                      {estadisticasRegistros.micronaire.optimalPercentage.toFixed(1)}%
                    </div>
                  </Card>
                </Col>
                <Col span={6}>
                  <Card size="small" style={{ textAlign: 'center', backgroundColor: theme.colors.errorLight }}>
                    <Text type="secondary" style={{ fontSize: '12px' }}>% Óptimo Longitud</Text>
                    <div style={{ fontSize: '24px', fontWeight: 600, color: theme.colors.error, marginTop: '8px' }}>
                      {estadisticasRegistros.longitud.optimalPercentage.toFixed(1)}%
                    </div>
                  </Card>
                </Col>
              </Row>
              
              {/* Tabla de Estadísticas Detalladas */}
              <Card size="small" style={{ marginTop: '16px' }}>
                <Row gutter={[16, 16]}>
                  <Col span={8}>
                    <div style={{ textAlign: 'center' }}>
                      <Text strong style={{ fontSize: '14px', display: 'block', marginBottom: '12px' }}>Micronaire</Text>
                      <Row gutter={8}>
                        <Col span={12}>
                          <Text type="secondary" style={{ fontSize: '11px' }}>Promedio</Text>
                          <div style={{ fontSize: '16px', fontWeight: 600 }}>{estadisticasRegistros.micronaire.mean.toFixed(2)}</div>
                        </Col>
                        <Col span={12}>
                          <Text type="secondary" style={{ fontSize: '11px' }}>Mediana</Text>
                          <div style={{ fontSize: '16px', fontWeight: 600 }}>{estadisticasRegistros.micronaire.median.toFixed(2)}</div>
                        </Col>
                      </Row>
                      <Row gutter={8} style={{ marginTop: '8px' }}>
                        <Col span={12}>
                          <Text type="secondary" style={{ fontSize: '11px' }}>Desv. Est.</Text>
                          <div style={{ fontSize: '14px' }}>{estadisticasRegistros.micronaire.stdDev.toFixed(2)}</div>
                        </Col>
                        <Col span={12}>
                          <Text type="secondary" style={{ fontSize: '11px' }}>CV (%)</Text>
                          <div style={{ fontSize: '14px' }}>{estadisticasRegistros.micronaire.cv.toFixed(2)}%</div>
                        </Col>
                      </Row>
                      <Row gutter={8} style={{ marginTop: '8px' }}>
                        <Col span={12}>
                          <Text type="secondary" style={{ fontSize: '11px' }}>Mín</Text>
                          <div style={{ fontSize: '14px' }}>{estadisticasRegistros.micronaire.minMax.min.toFixed(2)}</div>
                        </Col>
                        <Col span={12}>
                          <Text type="secondary" style={{ fontSize: '11px' }}>Máx</Text>
                          <div style={{ fontSize: '14px' }}>{estadisticasRegistros.micronaire.minMax.max.toFixed(2)}</div>
                        </Col>
                      </Row>
                    </div>
                  </Col>
                  <Col span={8}>
                    <div style={{ textAlign: 'center' }}>
                      <Text strong style={{ fontSize: '14px', display: 'block', marginBottom: '12px' }}>Longitud (mm)</Text>
                      <Row gutter={8}>
                        <Col span={12}>
                          <Text type="secondary" style={{ fontSize: '11px' }}>Promedio</Text>
                          <div style={{ fontSize: '16px', fontWeight: 600 }}>{estadisticasRegistros.longitud.mean.toFixed(2)}</div>
                        </Col>
                        <Col span={12}>
                          <Text type="secondary" style={{ fontSize: '11px' }}>Mediana</Text>
                          <div style={{ fontSize: '16px', fontWeight: 600 }}>{estadisticasRegistros.longitud.median.toFixed(2)}</div>
                        </Col>
                      </Row>
                      <Row gutter={8} style={{ marginTop: '8px' }}>
                        <Col span={12}>
                          <Text type="secondary" style={{ fontSize: '11px' }}>Desv. Est.</Text>
                          <div style={{ fontSize: '14px' }}>{estadisticasRegistros.longitud.stdDev.toFixed(2)}</div>
                        </Col>
                        <Col span={12}>
                          <Text type="secondary" style={{ fontSize: '11px' }}>CV (%)</Text>
                          <div style={{ fontSize: '14px' }}>{estadisticasRegistros.longitud.cv.toFixed(2)}%</div>
                        </Col>
                      </Row>
                      <Row gutter={8} style={{ marginTop: '8px' }}>
                        <Col span={12}>
                          <Text type="secondary" style={{ fontSize: '11px' }}>Mín</Text>
                          <div style={{ fontSize: '14px' }}>{estadisticasRegistros.longitud.minMax.min.toFixed(2)}</div>
                        </Col>
                        <Col span={12}>
                          <Text type="secondary" style={{ fontSize: '11px' }}>Máx</Text>
                          <div style={{ fontSize: '14px' }}>{estadisticasRegistros.longitud.minMax.max.toFixed(2)}</div>
                        </Col>
                      </Row>
                    </div>
                  </Col>
                  <Col span={8}>
                    <div style={{ textAlign: 'center' }}>
                      <Text strong style={{ fontSize: '14px', display: 'block', marginBottom: '12px' }}>Resistencia (g/tex)</Text>
                      <Row gutter={8}>
                        <Col span={12}>
                          <Text type="secondary" style={{ fontSize: '11px' }}>Promedio</Text>
                          <div style={{ fontSize: '16px', fontWeight: 600 }}>{estadisticasRegistros.resistencia.mean.toFixed(2)}</div>
                        </Col>
                        <Col span={12}>
                          <Text type="secondary" style={{ fontSize: '11px' }}>Mediana</Text>
                          <div style={{ fontSize: '16px', fontWeight: 600 }}>{estadisticasRegistros.resistencia.median.toFixed(2)}</div>
                        </Col>
                      </Row>
                      <Row gutter={8} style={{ marginTop: '8px' }}>
                        <Col span={12}>
                          <Text type="secondary" style={{ fontSize: '11px' }}>Desv. Est.</Text>
                          <div style={{ fontSize: '14px' }}>{estadisticasRegistros.resistencia.stdDev.toFixed(2)}</div>
                        </Col>
                        <Col span={12}>
                          <Text type="secondary" style={{ fontSize: '11px' }}>CV (%)</Text>
                          <div style={{ fontSize: '14px' }}>{estadisticasRegistros.resistencia.cv.toFixed(2)}%</div>
                        </Col>
                      </Row>
                      <Row gutter={8} style={{ marginTop: '8px' }}>
                        <Col span={12}>
                          <Text type="secondary" style={{ fontSize: '11px' }}>Mín</Text>
                          <div style={{ fontSize: '14px' }}>{estadisticasRegistros.resistencia.minMax.min.toFixed(2)}</div>
                        </Col>
                        <Col span={12}>
                          <Text type="secondary" style={{ fontSize: '11px' }}>Máx</Text>
                          <div style={{ fontSize: '14px' }}>{estadisticasRegistros.resistencia.minMax.max.toFixed(2)}</div>
                        </Col>
                      </Row>
                    </div>
                  </Col>
                </Row>
              </Card>
            </div>
          )}

          {/* Sección: Detalle y Resumen */}
          <div style={{ marginTop: '8px' }}>
            <Row gutter={[16, 16]}>
              {/* Sección Detalle */}
              <Col span={12}>
                <div style={{ 
                  backgroundColor: '#ffffff',
                  borderRadius: theme.styles.borderRadius,
                  boxShadow: theme.styles.boxShadow,
                  border: `1px solid ${theme.colors.borderLight}`,
                  padding: '12px',
                  minHeight: '280px',
                  maxHeight: '280px',
                  display: 'flex',
                  flexDirection: 'column'
                }}>
            {/* Contenedor principal con scroll */}
            <div style={{ 
              flex: 1,
              overflowY: 'auto',
              marginBottom: '8px'
            }}>
              {registros.length === 0 ? (
                <div style={{ 
                  textAlign: 'center', 
                  color: theme.colors.textSecondary,
                  padding: '20px',
                  fontSize: '12px'
                }}>
                  <Text type="secondary">
                    Use el botón "Generar Registros" para crear las muestras
                  </Text>
                </div>
              ) : (
                registros.map((registro, index) => (
                  <div 
                    key={index} 
                    style={{ 
                      display: 'flex', 
                      alignItems: 'center', 
                      padding: '6px',
                      backgroundColor: registro.isDuplicado
                        ? theme.colors.errorLight
                        : index % 2 === 0 ? theme.colors.backgroundGray : 'transparent',
                      marginBottom: '3px',
                      borderRadius: theme.styles.borderRadius,
                      border: registro.isDuplicado ? `1px solid ${theme.colors.error}` : 'none',
                      transition: 'background-color 0.2s ease'
                    }}
                    onMouseEnter={(e) => {
                      if (!registro.isDuplicado) {
                        e.currentTarget.style.backgroundColor = '#F5F8FB';
                      }
                    }}
                    onMouseLeave={(e) => {
                      if (!registro.isDuplicado) {
                        e.currentTarget.style.backgroundColor = index % 2 === 0 ? theme.colors.backgroundGray : 'transparent';
                      }
                    }}
                  >
                    <Checkbox
                      checked={registro.checked}
                      onChange={(e) => handleCheckboxChange(index, e.target.checked)}
                      disabled={registro.isDuplicado}
                    />
                    <span style={{ 
                      marginLeft: '6px',
                      fontFamily: 'monospace',
                      fontSize: '12px',
                      color: registro.isDuplicado ? theme.colors.error : theme.colors.textPrimary,
                      fontWeight: registro.isDuplicado ? 500 : 400
                    }}>
                      {`${registro.nroMuestra} | ${registro.calidad} | ${registro.micronaire.toFixed(2)} | ${registro.longitud.toFixed(2)} | ${registro.resistencia.toFixed(2)}`}
                    </span>
                    {registro.isDuplicado && (
                      <Tooltip title="Registro duplicado">
                        <span style={{ 
                          marginLeft: '6px', 
                          color: theme.colors.error,
                          fontSize: '11px'
                        }}>
                          ⚠️
                        </span>
                      </Tooltip>
                    )}
                  </div>
                ))
              )}
            </div>

            {/* Contador de muestras seleccionadas dentro del Card */}
            {registros.length > 0 && (
              <div style={{ 
                borderTop: `1px solid ${theme.colors.borderLight}`,
                paddingTop: '8px',
                paddingBottom: '6px',
                textAlign: 'center',
                backgroundColor: theme.colors.backgroundGray,
                borderRadius: '0 0 8px 8px'
              }}>
                <Text strong style={{ color: theme.colors.textPrimary, fontSize: '12px' }}>
                  Total: <span style={{ color: theme.colors.primary }}>{totalChecked}</span> de <span style={{ color: theme.colors.textSecondary }}>{registros.length}</span>
                </Text>
              </div>
            )}
                </div>
                
                {/* Botón Resumir */}
                <div style={{ marginTop: '8px' }} className="clasificacion-buttons">
                  <Button 
                    type="primary"
                    onClick={handleResumen}
                    disabled={registros.length === 0 || totalChecked === 0}
                    size="small"
                    style={{ 
                      width: '100%',
                      backgroundColor: theme.colors.primary,
                      borderColor: theme.colors.primary,
                      fontWeight: 500
                    }}
                  >
                    Resumir ({totalChecked} seleccionadas)
                  </Button>
                </div>
              </Col>

              {/* Sección Resumen */}
              <Col span={12}>
                <div style={{ 
                  backgroundColor: '#ffffff',
                  borderRadius: theme.styles.borderRadius,
                  boxShadow: theme.styles.boxShadow,
                  border: `1px solid ${theme.colors.borderLight}`,
                  padding: '12px',
                  display: 'flex',
                  flexDirection: 'column',
                  minHeight: '280px',
                  maxHeight: '280px'
                }}>
            {resumenes.length > 0 ? (
              <>
                <div style={{ 
                  flex: 1,
                  overflowY: 'auto',
                  marginBottom: '8px'
                }}>
                  <Radio.Group 
                    value={selectedResumen}
                    onChange={(e) => handleResumenSelect(e.target.value)}
                    name="resumenGroup"
                  >
                    {resumenes.map((resumen) => (
                      <div 
                        key={resumen.id}
                        style={{ 
                          backgroundColor: selectedResumen === resumen.id ? theme.colors.infoLight : theme.colors.backgroundGray,
                          padding: '8px',
                          borderRadius: theme.styles.borderRadius,
                          marginBottom: '6px',
                          display: 'flex',
                          alignItems: 'flex-start',
                          width: '100%',
                          border: selectedResumen === resumen.id ? `2px solid ${theme.colors.primary}` : `1px solid ${theme.colors.borderLight}`,
                          transition: 'all 0.2s ease',
                          cursor: 'pointer'
                        }}
                        onClick={() => handleResumenSelect(resumen.id)}
                        onMouseEnter={(e) => {
                          if (selectedResumen !== resumen.id) {
                            e.currentTarget.style.backgroundColor = '#F5F8FB';
                          }
                        }}
                        onMouseLeave={(e) => {
                          if (selectedResumen !== resumen.id) {
                            e.currentTarget.style.backgroundColor = theme.colors.backgroundGray;
                          }
                        }}
                      >
                        <div style={{ 
                          display: 'flex', 
                          alignItems: 'flex-start',
                          gap: '6px',
                          width: '100%'
                        }}>
                          <Radio 
                            value={resumen.id} 
                            checked={selectedResumen === resumen.id}
                            style={{ marginTop: '2px' }}
                          />
                          <div style={{ 
                            fontFamily: 'monospace',
                            fontSize: '12px',
                            whiteSpace: 'pre-wrap',
                            wordBreak: 'break-word',
                            flex: 1,
                            color: theme.colors.textPrimary,
                            lineHeight: '1.4'
                          }}>
                            {resumen.texto}
                          </div>
                        </div>
                      </div>
                    ))}
                  </Radio.Group>
                </div>
                <div style={{
                  borderTop: `1px solid ${theme.colors.borderLight}`,
                  paddingTop: '8px',
                  paddingBottom: '6px',
                  textAlign: 'center',
                  backgroundColor: theme.colors.backgroundGray,
                  borderRadius: '0 0 8px 8px'
                }}>
                  <Text strong style={{ color: theme.colors.textPrimary, fontSize: '12px' }}>
                    Muestras: <span style={{ color: theme.colors.primary }}>{resumenes.reduce((total, resumen) => total + resumen.cantidadSeleccionada, 0)}</span>
                  </Text>
                </div>
              </>
            ) : (
              <div style={{ 
                textAlign: 'center', 
                color: theme.colors.textSecondary, 
                marginTop: '20px',
                padding: '20px'
              }}>
                <Text type="secondary" style={{ fontSize: '12px' }}>
                  Use el botón "Resumir" para generar el resumen
                </Text>
              </div>
            )}
                </div>

                {/* Botones en el cuadrante derecho */}
                <div style={{ marginTop: '8px' }} className="clasificacion-buttons">
                  <Space size="small" style={{ width: '100%', justifyContent: 'center' }}>
                    <Button 
                      onClick={handleCancelar}
                      size="small"
                      disabled={loading}
                      style={{ 
                        fontWeight: 500,
                        minWidth: '80px'
                      }}
                    >
                      Cancelar
                    </Button>
                    <Button 
                      type="primary"
                      style={{ 
                        backgroundColor: theme.colors.primary,
                        borderColor: theme.colors.primary,
                        fontWeight: 500,
                        minWidth: '100px'
                      }}
                      onClick={handleGrabarTodo}
                      loading={loading}
                      disabled={resumenes.length === 0}
                      size="small"
                    >
                      {loading ? 'Grabando...' : 'Grabar Todo'}
                    </Button>
                    <Button
                      type="primary"
                      style={{
                        backgroundColor: theme.colors.primary,
                        borderColor: theme.colors.primary,
                        fontWeight: 500,
                        minWidth: '80px'
                      }}
                      onClick={handleModificar}
                      disabled={!canModify || loading}
                      size="small"
                    >
                      Modificar
                    </Button>
                  </Space>
                </div>
              </Col>
            </Row>
          </div>
        </Card>
      </div>
    </App>
  );
};

export default FormularioClasificacion;
